import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from './ToastContext';
import './ProjectForm.css';

const ProjectForm = () => {
  const navigate = useNavigate();
  
  const API_BASE_URL = 'https://eks-projectmanager.eksconstruction.in/api';
  
  const [formData, setFormData] = useState({
    projectType: 'Ongoing Projects',
    clientName: '',
    phase: '',
    location: '',
    description: '',
    area: '',
    progress: 35,
    startDate: '',
    expectedDate: '',
    completedDate: '',
    launchDate: '',
    floors: '',
    keyFeatures: '',
    images: []
  });

  const [errors, setErrors] = useState({});
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showToast } = useToast();

  // Reset form fields when project type changes
  useEffect(() => {
    if (formData.projectType === 'Completed Projects') {
      setFormData(prev => ({
        ...prev,
        progress: 100,
        phase: '',
        startDate: '',
        expectedDate: '',
        launchDate: ''
      }));
    } else if (formData.projectType === 'Upcoming Projects') {
      setFormData(prev => ({
        ...prev,
        progress: 0,
        phase: '',
        expectedDate: '',
        completedDate: ''
      }));
    } else {
      // Ongoing Projects
      setFormData(prev => ({
        ...prev,
        completedDate: '',
        launchDate: ''
      }));
    }
  }, [formData.projectType]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'area') {
      if (value !== '' && !/^\d+$/.test(value)) {
        return;
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleProgressChange = (e) => {
    setFormData(prev => ({
      ...prev,
      progress: parseInt(e.target.value)
    }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setFormData(prev => ({
      ...prev,
      images: [...prev.images, ...files]
    }));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    setFormData(prev => ({
      ...prev,
      images: [...prev.images, ...files]
    }));
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.clientName.trim()) {
      newErrors.clientName = 'Client name is required';
    } else if (!/^[a-zA-Z\s]+$/.test(formData.clientName)) {
      newErrors.clientName = 'Client name should contain only letters';
    }
    
    if (!formData.location.trim()) {
      newErrors.location = 'Location is required';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    
    if (!formData.area.trim()) {
      newErrors.area = 'Area is required';
    }
    
    // Type-specific validation
    if (formData.projectType === 'Ongoing Projects') {
      if (!formData.phase.trim()) newErrors.phase = 'Phase is required';
      if (!formData.startDate) newErrors.startDate = 'Start date is required';
      if (!formData.expectedDate) newErrors.expectedDate = 'Expected date is required';
    } else if (formData.projectType === 'Completed Projects') {
      if (!formData.completedDate) newErrors.completedDate = 'Completed date is required';
    } else if (formData.projectType === 'Upcoming Projects') {
      if (!formData.launchDate) newErrors.launchDate = 'Launch date is required';
      if (!formData.startDate) newErrors.startDate = 'Start date is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('projectType', formData.projectType);
      formDataToSend.append('clientName', formData.clientName);
      formDataToSend.append('projectTitle', formData.description); // Using description as title
      formDataToSend.append('location', formData.location);
      formDataToSend.append('area', formData.area);
      formDataToSend.append('floors', formData.floors);
      formDataToSend.append('phase', formData.phase);
      
      // Date handling based on project type
      if (formData.projectType === 'Ongoing Projects') {
        formDataToSend.append('startDate', formData.startDate);
        formDataToSend.append('completionDate', formData.expectedDate);
      } else if (formData.projectType === 'Completed Projects') {
        formDataToSend.append('startDate', formData.completedDate);
        formDataToSend.append('completionDate', formData.completedDate);
      } else if (formData.projectType === 'Upcoming Projects') {
        formDataToSend.append('startDate', formData.launchDate);
        formDataToSend.append('completionDate', formData.startDate);
      }
      
      formDataToSend.append('progress', formData.progress);
      formDataToSend.append('keyFeatures', formData.keyFeatures);

      formData.images.forEach((image) => {
        formDataToSend.append('images[]', image);
      });

      const response = await fetch(`${API_BASE_URL}/insert_data.php`, {
        method: 'POST',
        body: formDataToSend
      });

      const result = await response.json();

      if (result.success) {
        showToast && showToast({ message: 'Project added successfully!', type: 'success' });
        navigate('/dashboard');
      } else {
        throw new Error(result.message || 'Failed to add project');
      }

    } catch (error) {
      console.error('Error adding project:', error);
      showToast && showToast({ message: 'Error: ' + error.message, type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };

  const isOngoing = formData.projectType === 'Ongoing Projects';
  const isCompleted = formData.projectType === 'Completed Projects';
  const isUpcoming = formData.projectType === 'Upcoming Projects';

  return (
    <div className="project-form-container">
      <div className="form-card">
        <div className="form-header">
          <div className="header-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <polyline points="9 22 9 12 15 12 15 22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h1>EKS Construction Project Manager</h1>
          <p className="subtitle">Manage your construction projects efficiently</p>
        </div>

        <form onSubmit={handleSubmit} className="project-form">
          {/* Project Type Selection */}
          <div className="form-section">
            <div className="project-type-row">
              <div className="project-type-group">
                <label htmlFor="projectType" className="form-label">
                  <span className="label-icon">📋</span>
                  Select Project Type
                </label>
                <select
                  id="projectType"
                  name="projectType"
                  value={formData.projectType}
                  onChange={handleInputChange}
                  className="form-select"
                  disabled={isSubmitting}
                >
                  <option value="Ongoing Projects">Ongoing Projects</option>
                  <option value="Completed Projects">Completed Projects</option>
                  <option value="Upcoming Projects">Upcoming Projects</option>
                </select>
              </div>
              
              <button 
                type="button" 
                className="back-to-dashboard-btn"
                onClick={handleBackToDashboard}
                disabled={isSubmitting}
              >
                <svg viewBox="0 0 24 24" fill="none" className="back-icon">
                  <path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Back to Dashboard
              </button>
            </div>
          </div>

          {/* Client Name - Common for all */}
          <div className="form-section">
            <div className="form-group">
              <label htmlFor="clientName" className="form-label">
                <span className="label-icon">👤</span>
                Client Name
              </label>
              <input
                type="text"
                id="clientName"
                name="clientName"
                value={formData.clientName}
                onChange={handleInputChange}
                placeholder="Enter client name"
                className={`form-input ${errors.clientName ? 'error' : ''}`}
                disabled={isSubmitting}
              />
              {errors.clientName && <span className="error-message">{errors.clientName}</span>}
            </div>
          </div>

          {/* Phase - Only for Ongoing */}
          {isOngoing && (
            <div className="form-section">
              <div className="form-group">
                <label htmlFor="phase" className="form-label">
                  <span className="label-icon">⚙️</span>
                  Project Phase/Site
                </label>
                <input
                  type="text"
                  id="phase"
                  name="phase"
                  value={formData.phase}
                  onChange={handleInputChange}
                  placeholder="e.g. Crowncity Phase-1"
                  className={`form-input ${errors.phase ? 'error' : ''}`}
                  disabled={isSubmitting}
                />
                {errors.phase && <span className="error-message">{errors.phase}</span>}
              </div>
            </div>
          )}

          {/* Location - Common for all */}
          <div className="form-section">
            <div className="form-group">
              <label htmlFor="location" className="form-label">
                <span className="label-icon">📍</span>
                Location
              </label>
              <input
                type="text"
                id="location"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                placeholder="Enter location"
                className={`form-input ${errors.location ? 'error' : ''}`}
                disabled={isSubmitting}
              />
              {errors.location && <span className="error-message">{errors.location}</span>}
            </div>
          </div>

          {/* Description - Common for all */}
          <div className="form-section">
            <div className="form-group">
              <label htmlFor="description" className="form-label">
                <span className="label-icon">📝</span>
                Project Description
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Enter project description"
                className={`form-textarea ${errors.description ? 'error' : ''}`}
                rows="4"
                disabled={isSubmitting}
              />
              {errors.description && <span className="error-message">{errors.description}</span>}
            </div>
          </div>

          {/* Area and Floors - Common for all */}
          <div className="form-section">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="area" className="form-label">
                  <span className="label-icon">📏</span>
                  Area (sqft)
                </label>
                <input
                  type="text"
                  id="area"
                  name="area"
                  value={formData.area}
                  onChange={handleInputChange}
                  placeholder="Enter area"
                  className={`form-input ${errors.area ? 'error' : ''}`}
                  disabled={isSubmitting}
                />
                {errors.area && <span className="error-message">{errors.area}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="floors" className="form-label">
                  <span className="label-icon">🏢</span>
                  Floors/Units
                </label>
                <input
                  type="text"
                  id="floors"
                  name="floors"
                  value={formData.floors}
                  onChange={handleInputChange}
                  placeholder="e.g. 2 Floors"
                  className="form-input"
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </div>

          {/* Progress - Only for Ongoing */}
          {isOngoing && (
            <div className="form-section">
              <div className="form-group progress-group">
                <label htmlFor="progress" className="form-label">
                  <span className="label-icon">📊</span>
                  Progress ({formData.progress}%)
                </label>
                <div className="progress-container">
                  <input
                    type="range"
                    id="progress"
                    name="progress"
                    min="0"
                    max="100"
                    value={formData.progress}
                    onChange={handleProgressChange}
                    className="progress-slider"
                    disabled={isSubmitting}
                  />
                  <div className="progress-bar-bg">
                    <div 
                      className="progress-bar-fill"
                      style={{ width: `${formData.progress}%` }}
                    >
                      <span className="progress-label">{formData.progress}%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Dates - Type specific */}
          {isOngoing && (
            <div className="form-section">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="startDate" className="form-label">
                    <span className="label-icon">🚀</span>
                    Started Date
                  </label>
                  <input
                    type="date"
                    id="startDate"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleInputChange}
                    className={`form-input date-input ${errors.startDate ? 'error' : ''}`}
                    disabled={isSubmitting}
                  />
                  {errors.startDate && <span className="error-message">{errors.startDate}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="expectedDate" className="form-label">
                    <span className="label-icon">🎯</span>
                    Expected Completion
                  </label>
                  <input
                    type="date"
                    id="expectedDate"
                    name="expectedDate"
                    value={formData.expectedDate}
                    onChange={handleInputChange}
                    className={`form-input date-input ${errors.expectedDate ? 'error' : ''}`}
                    disabled={isSubmitting}
                  />
                  {errors.expectedDate && <span className="error-message">{errors.expectedDate}</span>}
                </div>
              </div>
            </div>
          )}

          {isCompleted && (
            <div className="form-section">
              <div className="form-group">
                <label htmlFor="completedDate" className="form-label">
                  <span className="label-icon">✅</span>
                  Completed Date
                </label>
                <input
                  type="date"
                  id="completedDate"
                  name="completedDate"
                  value={formData.completedDate}
                  onChange={handleInputChange}
                  className={`form-input date-input ${errors.completedDate ? 'error' : ''}`}
                  disabled={isSubmitting}
                />
                {errors.completedDate && <span className="error-message">{errors.completedDate}</span>}
              </div>
            </div>
          )}

          {isUpcoming && (
            <div className="form-section">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="launchDate" className="form-label">
                    <span className="label-icon">🎪</span>
                    Launch Date
                  </label>
                  <input
                    type="date"
                    id="launchDate"
                    name="launchDate"
                    value={formData.launchDate}
                    onChange={handleInputChange}
                    className={`form-input date-input ${errors.launchDate ? 'error' : ''}`}
                    disabled={isSubmitting}
                  />
                  {errors.launchDate && <span className="error-message">{errors.launchDate}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="startDate" className="form-label">
                    <span className="label-icon">🚀</span>
                    Start Date
                  </label>
                  <input
                    type="date"
                    id="startDate"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleInputChange}
                    className={`form-input date-input ${errors.startDate ? 'error' : ''}`}
                    disabled={isSubmitting}
                  />
                  {errors.startDate && <span className="error-message">{errors.startDate}</span>}
                </div>
              </div>
            </div>
          )}

          {/* Key Features - Common for all */}
          <div className="form-section">
            <div className="form-group">
              <label htmlFor="keyFeatures" className="form-label">
                <span className="label-icon">⭐</span>
                Key Features
              </label>
              <textarea
                id="keyFeatures"
                name="keyFeatures"
                value={formData.keyFeatures}
                onChange={handleInputChange}
                placeholder="Comma separated features (e.g., Swimming Pool, Gym, Garden)"
                className="form-textarea"
                rows="3"
                disabled={isSubmitting}
              />
            </div>
          </div>

          {/* Upload Images - Common for all */}
          <div className="form-section">
            <div className="form-group">
              <label className="form-label">
                <span className="label-icon">📸</span>
                Upload Images
              </label>
              <div 
                className={`upload-area ${isDragging ? 'dragging' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  id="fileInput"
                  multiple
                  accept="image/*"
                  onChange={handleFileChange}
                  className="file-input"
                  disabled={isSubmitting}
                />
                <label htmlFor="fileInput" className="upload-label">
                  <svg className="upload-icon" viewBox="0 0 24 24" fill="none">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="17 8 12 3 7 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <line x1="12" y1="3" x2="12" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <p className="upload-text">
                    <span className="upload-main">Click to upload</span> or drag and drop
                  </p>
                  <p className="upload-hint">PNG, JPG, GIF up to 10MB</p>
                </label>
              </div>
              
              {formData.images.length > 0 && (
                <div className="image-preview-container">
                  {formData.images.map((file, index) => (
                    <div key={index} className="image-preview">
                      <img 
                        src={URL.createObjectURL(file)} 
                        alt={`Preview ${index + 1}`} 
                      />
                      <button 
                        type="button"
                        onClick={() => removeImage(index)}
                        className="remove-image-btn"
                        disabled={isSubmitting}
                      >
                        ×
                      </button>
                      <span className="image-name">{file.name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className="form-actions">
            <button type="submit" className="submit-btn" disabled={isSubmitting}>
              <span className="btn-icon">{isSubmitting ? '⏳' : '✓'}</span>
              {isSubmitting ? 'Adding Project...' : 'Add Project'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProjectForm;
